/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Board Finder 2026 - JavaScript Controller
 * Handles wizard navigation, filtering, and product display
 */

class BoardFinder {
  constructor(container) {
    this.container = container || document.getElementById("BoardFinder");
    if (!this.container) return;
    this.events = new AbortController();
    this.timers = new Set();
    this.resultsRequestId = 0;

    try {
      this.config = JSON.parse(this.container.querySelector("#bf-config")?.textContent || "{}");
      this.products = JSON.parse(this.container.querySelector("#bf-products")?.textContent || "[]");
      if (!this.config || typeof this.config !== "object" || Array.isArray(this.config)) this.config = {};
      if (!Array.isArray(this.products)) this.products = [];
      this.products = this.products.filter((product) => product && typeof product === "object");
    } catch (error) {
      this.config = {};
      this.products = [];
    }

    // Pre-classify all products using the BoardFinderData helper (data file loaded before this script)
    const dataLib = window.BoardFinderData;
    if (dataLib) {
      this.products.forEach((p) => {
        p._meta = dataLib.classifyProduct(p);
      });
      const unclassified = this.products.filter((p) => !p._meta.disciplines.length).length;
      if (unclassified > 0) {
        console.warn(
          `[BoardFinder] ${unclassified}/${this.products.length} products have no discipline classification (unknown family in MODEL_PROFILES). They will be excluded from filtered results.`,
        );
      }
    } else {
      console.error(
        "[BoardFinder] BoardFinderData helper missing -- board-finder-data.js failed to load. Filtering disabled.",
      );
    }

    this.state = {
      currentStep: 1,
      totalSteps: 0,
      selections: {
        level: null,
        discipline: null,
        weight: null,
        budget: null,
      },
      compareList: [],
    };

    this.init();
    this.productsReady = this.loadProductPages();
  }

  async loadProductPages() {
    const pool = this.container.querySelector("#bf-products");
    const pages = Number(pool?.dataset.pages) || 1;
    const current = Number(pool?.dataset.currentPage) || 1;
    try {
      const products = current === 1 ? this.products.slice() : [];
      for (let page = 1; page <= pages; page++) {
        if (page === current) {
          if (current !== 1) products.push(...this.products);
          continue;
        }
        const url = new URL(window.location.href);
        url.searchParams.set("section_id", pool.dataset.sectionId);
        url.searchParams.set("page", String(page));
        const response = await fetch(url, { signal: this.events.signal });
        if (!response.ok) throw new Error("Product pool request failed");
        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const pageProducts = JSON.parse(doc.querySelector("#bf-products")?.textContent || "null");
        if (!Array.isArray(pageProducts)) throw new Error("Invalid product pool");
        products.push(...pageProducts);
      }
      const unique = new Map(products.filter((product) => product?.id).map((product) => [String(product.id), product]));
      this.products = Array.from(unique.values());
      if (window.BoardFinderData) this.products.forEach((product) => { product._meta = window.BoardFinderData.classifyProduct(product); });
      this.loadFailed = false;
    } catch (error) {
      this.loadFailed = true;
    }
  }

  listen(target, type, handler) {
    target?.addEventListener(type, handler, { signal: this.events.signal });
  }

  delay(handler, ms) {
    const timer = setTimeout(() => { this.timers.delete(timer); handler(); }, ms);
    this.timers.add(timer);
  }

  destroy() {
    this.events?.abort();
    this.timers?.forEach(clearTimeout);
    if (this.container?.classList.contains("is-open")) this.closeOverlay();
  }

  init() {
    this.cacheElements();
    this.countSteps();
    this.bindEvents();
    this.updateProgress();
    this.initWeightSlider();

    if (this.container.dataset.displayMode === "overlay") {
      this.initOverlay();
    }

    this.updateCompareBar();
  }

  cacheElements() {
    this.steps = this.container.querySelectorAll(".bf-step");
    this.progressFill = document.getElementById("bfProgressFill");
    this.progressSteps = this.container.querySelectorAll(".bf-progress-step");
    this.resultsScreen = document.getElementById("bfResults");
    this.resultsGrid = document.getElementById("bfResultsGrid");
    this.resultsSummary = document.getElementById("bfResultsSummary");
    this.resultsCount = document.getElementById("bfResultsCount");
    this.noResults = document.getElementById("bfNoResults");
    this.compareBar = document.getElementById("bfCompareBar");
    this.compareCount = document.getElementById("bfCompareCount");
    this.compareBtn = document.getElementById("bfCompareBtn");
    this.weightSlider = document.getElementById("bfWeightSlider");
    this.weightValue = document.getElementById("bfWeightValue");
    this.weightFill = document.getElementById("bfWeightFill");
  }

  countSteps() {
    this.state.totalSteps = this.steps.length;
  }

  bindEvents() {
    // Card selections (click + keyboard for a11y -- cards have role="button" + tabindex="0")
    this.container.querySelectorAll(".bf-level-card, .bf-discipline-card, .bf-budget-card").forEach((card) => {
      this.listen(card, "click", (e) => this.handleCardSelect(e));
      this.listen(card, "keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.handleCardSelect(e);
        }
      });
    });

    // Navigation buttons
    this.container.querySelectorAll("[data-action]").forEach((btn) => {
      this.listen(btn, "click", (e) => this.handleAction(e));
    });

    // Weight presets
    this.container.querySelectorAll(".bf-weight-preset").forEach((btn) => {
      this.listen(btn, "click", (e) => this.handleWeightPreset(e));
    });

    // Compare buttons (delegated)
    this.listen(this.resultsGrid, "click", (e) => {
      if (e.target.closest('[data-action="compare"]')) {
        this.handleCompare(e.target.closest('[data-action="compare"]'));
      }
    });

    if (this.compareBtn) {
      this.listen(this.compareBtn, "click", () => this.handleCompareNow());
    }

    this.listen(document, "compare:updated", () => this.updateCompareBar());
  }

  handleCardSelect(e) {
    const card = e.currentTarget;
    const parent = card.parentElement;
    const step = card.closest(".bf-step");
    const stepType = step?.dataset.stepType;
    const value = card.dataset.value;

    // Deselect siblings (clears aria-pressed for a11y, keeps role="button" semantics)
    parent.querySelectorAll(".bf-level-card, .bf-discipline-card, .bf-budget-card").forEach((c) => {
      c.classList.remove("is-selected");
      c.setAttribute("aria-pressed", "false");
    });

    // Select this card
    card.classList.add("is-selected");
    card.setAttribute("aria-pressed", "true");

    // Store selection
    if (stepType && value) {
      this.state.selections[stepType] = value;
    }

    // Enable next button
    step?.querySelectorAll('[data-action="next"], [data-action="results"]').forEach((button) => {
      button.disabled = false;
    });
  }

  handleAction(e) {
    const action = e.currentTarget.dataset.action;

    switch (action) {
      case "next":
        this.nextStep();
        break;
      case "back":
        this.prevStep();
        break;
      case "skip":
        this.skipStep();
        break;
      case "results":
        this.showResults();
        break;
      case "restart":
        this.restart();
        break;
    }
  }

  nextStep() {
    if (this.state.currentStep < this.state.totalSteps) {
      this.goToStep(this.state.currentStep + 1);
    } else {
      this.showResults();
    }
  }

  prevStep() {
    if (this.state.currentStep > 1) {
      this.goToStep(this.state.currentStep - 1);
    }
  }

  skipStep() {
    this.nextStep();
  }

  goToStep(stepNumber) {
    this.resultsRequestId++;
    this.container.removeAttribute("aria-busy");
    // Hide current step
    this.steps.forEach((step) => step.classList.remove("is-active"));

    // Show target step
    const targetStep = this.container.querySelector(`.bf-step[data-step="${stepNumber}"]`);
    if (targetStep) {
      targetStep.classList.add("is-active");
      this.state.currentStep = stepNumber;
      this.updateProgress();
    }
  }

  updateProgress() {
    const progress = (this.state.currentStep / this.state.totalSteps) * 100;

    if (this.progressFill) {
      this.progressFill.style.width = `${progress}%`;
    }

    this.progressSteps.forEach((step, i) => {
      step.classList.remove("is-active", "is-completed");
      if (i + 1 < this.state.currentStep) {
        step.classList.add("is-completed");
      } else if (i + 1 === this.state.currentStep) {
        step.classList.add("is-active");
      }
    });
  }

  // Weight slider
  initWeightSlider() {
    if (!this.weightSlider) return;

    const min = parseInt(this.weightSlider.dataset.min) || 50;
    const max = parseInt(this.weightSlider.dataset.max) || 120;
    const value = parseInt(this.weightSlider.value) || 75;

    // Capture default for restart() and capture into state so users who never touch slider still get a weight (fix P0-5)
    this.weightDefault = value;
    this.state.selections.weight = value;
    this.updateWeightDisplay(value, min, max);

    this.listen(this.weightSlider, "input", (e) => {
      const val = parseInt(e.target.value);
      this.updateWeightDisplay(val, min, max);
      this.state.selections.weight = val;
    });
  }

  updateWeightDisplay(value, min, max) {
    if (this.weightValue) {
      this.weightValue.textContent = value;
    }
    if (this.weightFill) {
      const percent = ((value - min) / (max - min)) * 100;
      this.weightFill.style.width = `${percent}%`;
    }
  }

  handleWeightPreset(e) {
    const weight = parseInt(e.currentTarget.dataset.weight);
    if (this.weightSlider && weight) {
      this.weightSlider.value = weight;
      const min = parseInt(this.weightSlider.dataset.min) || 50;
      const max = parseInt(this.weightSlider.dataset.max) || 120;
      this.updateWeightDisplay(weight, min, max);
      this.state.selections.weight = weight;
    }
  }

  // Results
  async showResults() {
    const requestId = ++this.resultsRequestId;
    this.container.setAttribute("aria-busy", "true");
    await this.productsReady;
    if (this.events.signal.aborted || requestId !== this.resultsRequestId) return;
    this.container.removeAttribute("aria-busy");
    let errorEl = this.container.querySelector("[data-pool-error]");
    if (this.loadFailed) {
      if (!errorEl) {
        errorEl = document.createElement("p");
        errorEl.setAttribute("data-pool-error", "");
        errorEl.setAttribute("role", "alert");
        this.container.appendChild(errorEl);
      }
      errorEl.textContent = this.config.translations?.loadError || "";
      errorEl.hidden = false;
      this.productsReady = this.loadProductPages();
      return;
    }
    if (errorEl) errorEl.hidden = true;
    // Hide all steps
    this.steps.forEach((step) => step.classList.remove("is-active"));

    // Filter products
    const filtered = this.filterProducts();

    // Render results
    this.renderResults(filtered);

    // Show results screen
    if (this.resultsScreen) {
      this.resultsScreen.classList.add("is-active");
    }

    // Update progress to complete
    if (this.progressFill) {
      this.progressFill.style.width = "100%";
    }
  }

  filterProducts() {
    const { level, discipline, budget, weight } = this.state.selections;
    const dataLib = window.BoardFinderData;
    const maxResults = this.config.maxResults || 6;

    // Per-rule predicates -- pure functions on a product's _meta
    const matchDiscipline = (p) => {
      if (!discipline) return true;
      const disciplines = p._meta?.disciplines || [];
      return disciplines.includes(discipline.toLowerCase());
    };

    const matchLevel = (p) => {
      if (!level) return true;
      const productLevels = p._meta?.levels || [];
      if (!productLevels.length) return false; // no level info -> exclude (no false positives)
      return dataLib ? dataLib.matchesLevel(productLevels, level) : true;
    };

    const matchBudget = (p) => {
      if (!budget) return true;
      const price = parseInt(p.price, 10);
      if (isNaN(price)) return true;
      // Shopify price is in cents -- convert
      const priceEur = price / 100;
      const budgetCard = this.container.querySelector(`.bf-budget-card[data-value="${budget}"]`);
      if (!budgetCard) return true;
      const minPrice = parseInt(budgetCard.dataset.min, 10) || 0;
      const maxPrice = parseInt(budgetCard.dataset.max, 10) || Infinity;
      return priceEur >= minPrice && priceEur <= maxPrice;
    };

    const matchWeight = (tolerance) => (p) => {
      if (!weight || !dataLib) return true;
      const productVol = p._meta?.volumeNum;
      if (!productVol) return true; // no volume data -> don't filter out
      const targetVol = dataLib.targetVolumeForWeight(weight, level || "intermediate");
      return Math.abs(productVol - targetVol) <= tolerance;
    };

    // Strict pass: all filters with weight tolerance ±20L
    let results = this.products.filter(
      (p) => matchDiscipline(p) && matchLevel(p) && matchBudget(p) && matchWeight(20)(p),
    );

    // Relax weight tolerance if zero results
    if (results.length === 0) {
      results = this.products.filter(
        (p) => matchDiscipline(p) && matchLevel(p) && matchBudget(p) && matchWeight(40)(p),
      );
    }
    // Relax weight entirely if still zero
    if (results.length === 0) {
      results = this.products.filter((p) => matchDiscipline(p) && matchLevel(p) && matchBudget(p));
    }
    // Relax budget if still zero
    if (results.length === 0) {
      results = this.products.filter((p) => matchDiscipline(p) && matchLevel(p));
    }
    // Last resort: discipline only
    if (results.length === 0) {
      results = this.products.filter((p) => matchDiscipline(p));
    }

    return results.slice(0, maxResults);
  }

  renderResults(products) {
    if (!this.resultsGrid) return;

    // Clear grid
    this.resultsGrid.innerHTML = "";

    // Update count
    if (this.resultsCount) {
      this.resultsCount.textContent = `${products.length} ${this.config.translations?.boardsMatch || "boards match your profile"}`;
    }

    // Render summary
    this.renderSummary();

    // No results
    if (products.length === 0) {
      if (this.noResults) this.noResults.style.display = "block";
      return;
    }

    if (this.noResults) this.noResults.style.display = "none";

    // Get template
    const template = document.getElementById("bfProductCardTemplate");
    if (!template) return;

    // Render products
    products.forEach((product, index) => {
      const card = template.content.cloneNode(true);
      const article = card.querySelector(".bf-product-card");

      article.dataset.productId = product.id;
      article.dataset.productHandle = product.handle || "";
      article.dataset.compareType = "board";
      article.dataset.productTitle = product.title || "";
      article.dataset.productPrice = product.priceFormatted || "";
      article.dataset.productImage = product.image || "";
      article.dataset.productUrl = product.url || "";

      // Image
      const img = card.querySelector(".bf-product-img");
      if (img && product.image) {
        img.src = product.image;
        img.alt = product.title;
      }

      // Best match badge (first item only)
      const badge = card.querySelector(".bf-product-card__badge");
      if (badge) {
        badge.style.display = index === 0 ? "block" : "none";
      }
      if (index === 0) {
        article.classList.add("bf-product-card--best");
      }

      // Text content -- prefer extracted family from title, then metafield, then vendor
      const family = card.querySelector(".bf-product-card__family");
      if (family) family.textContent = product._meta?.family || product.family || product.vendor || "";

      const name = card.querySelector(".bf-product-card__name");
      if (name) name.textContent = product.title;

      const volume = card.querySelector(".bf-spec-volume");
      const vol = product._meta?.volumeNum || product.volume;
      if (volume) volume.textContent = vol ? `${vol}L` : "-";

      const construction = card.querySelector(".bf-spec-construction");
      if (construction) construction.textContent = product.construction || "-";

      // Reason
      const reason = card.querySelector(".bf-product-card__reason");
      if (reason && this.config.showReason) {
        reason.textContent = this.generateReason(product, index);
      }

      // Price
      const price = card.querySelector(".bf-product-card__price");
      if (price) price.textContent = product.priceFormatted || "";

      // CTA link
      const cta = card.querySelector(".bf-product-card__cta");
      if (cta) cta.href = product.url;

      const compareBtn = card.querySelector('[data-action="compare"]');
      if (compareBtn && this.isProductInCompare(product.id)) {
        compareBtn.classList.add("is-active");
      }

      this.resultsGrid.appendChild(card);
    });
  }

  isProductInCompare(productId) {
    if (window.ExocetCompare) {
      return window.ExocetCompare.isSelected(productId);
    }
    return this.state.compareList.includes(String(productId));
  }

  buildCompareItem(product) {
    return {
      id: String(product.id),
      type: "board",
      handle: product.handle,
      title: product.title,
      price: product.priceFormatted || "",
      image: product.image || "",
      url: product.url || "",
    };
  }

  // Interpolates {{ key }} placeholders in a locale template string.
  fillReason(template, params) {
    return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
      params[key] != null ? params[key] : match
    );
  }

  // Resolves a wizard slug (beginner, freeride, ...) to its localized label.
  reasonLabel(map, slug) {
    if (slug && map && map[slug]) return map[slug];
    return this.capitalize(slug || "");
  }

  generateReason(product, index) {
    const { level, discipline, weight } = this.state.selections;
    const t = this.config.translations || {};
    const levelLabel = this.reasonLabel(t.levelLabels, level);
    const disciplineLabel = this.reasonLabel(t.disciplineLabels, discipline);

    if (index === 0) {
      if (weight && product.volume) {
        return this.fillReason(
          t.reasonPerfectVolume ||
            "Perfect volume for your weight. {{ construction }} construction offers excellent performance.",
          { construction: product.construction || "Quality" }
        );
      }
      return this.fillReason(
        t.reasonIdealChoice ||
          "Ideal choice for {{ discipline }}. Great match for {{ level }} level riders.",
        { discipline: disciplineLabel || "your style", level: levelLabel || "your" }
      );
    }

    if (product.construction?.toLowerCase() === "silver") {
      return (
        t.reasonSilverValue ||
        "Same performance at a more accessible price point. Excellent durability."
      );
    }

    if (product.volume && weight) {
      const diff = parseInt(product.volume) - weight;
      if (diff > 30) {
        return (
          t.reasonExtraVolume || "Extra volume for enhanced light wind performance."
        );
      }
    }

    return this.fillReason(
      t.reasonQualityOption || "Quality option for {{ discipline }} riding.",
      { discipline: disciplineLabel || "versatile" }
    );
  }

  renderSummary() {
    if (!this.resultsSummary) return;

    const { level, discipline, weight, budget } = this.state.selections;
    const t = this.config.translations || {};
    const labels = {
      level: t.summaryLevel || "Level",
      discipline: t.summaryDiscipline || "Discipline",
      weight: t.summaryWeight || "Weight",
      budget: t.summaryBudget || "Budget",
    };

    // Build with DOM API (safer than innerHTML even with controlled inputs)
    this.resultsSummary.replaceChildren();
    const appendTag = (labelText, valueText) => {
      const tag = document.createElement("div");
      tag.className = "bf-summary-tag";
      const label = document.createElement("span");
      label.className = "bf-summary-label";
      label.textContent = `${labelText}:`;
      const value = document.createElement("span");
      value.className = "bf-summary-value";
      value.textContent = valueText;
      tag.appendChild(label);
      tag.appendChild(value);
      this.resultsSummary.appendChild(tag);
    };

    if (level) appendTag(labels.level, this.capitalize(level));
    if (discipline) appendTag(labels.discipline, this.capitalize(discipline));
    if (weight) appendTag(labels.weight, `${weight} kg`);
    if (budget) appendTag(labels.budget, this.capitalize(budget));
  }

  // Compare
  handleCompare(btn) {
    const card = btn.closest(".bf-product-card");
    const productId = card?.dataset.productId;
    if (!productId) return;

    const product = this.products.find((p) => String(p.id) === String(productId));

    if (window.ExocetCompare && product) {
      if (window.ExocetCompare.isSelected(productId)) {
        window.ExocetCompare.remove(productId);
        btn.classList.remove("is-active");
      } else {
        const result = window.ExocetCompare.add(this.buildCompareItem(product));
        if (result.ok) {
          btn.classList.add("is-active");
        } else {
          btn.classList.remove("is-active");
        }
      }
      this.updateCompareBar();
      return;
    }

    const isActive = btn.classList.contains("is-active");

    if (isActive) {
      btn.classList.remove("is-active");
      this.state.compareList = this.state.compareList.filter((id) => id !== productId);
    } else {
      if (this.state.compareList.length >= 3) {
        return;
      }
      btn.classList.add("is-active");
      this.state.compareList.push(productId);
    }

    this.updateCompareBar();
  }

  handleCompareNow() {
    if (window.ExocetCompare) {
      if (window.ExocetCompare.getCount() >= 2) {
        window.ExocetCompare.navigateToCompare();
      }
      return;
    }

    if (this.state.compareList.length < 2) return;

    const comparePageUrl = this.config.comparePageUrl || "/pages/compare";
    const params = new URLSearchParams();
    params.set("ids", this.state.compareList.join(","));
    params.set("type", "board");
    window.location.href = comparePageUrl + "?" + params.toString();
  }

  updateCompareBar() {
    const count = window.ExocetCompare ? window.ExocetCompare.getCount() : this.state.compareList.length;

    if (this.compareCount) {
      this.compareCount.textContent = count;
    }

    if (this.compareBar) {
      this.compareBar.classList.toggle("is-active", count > 0);
    }

    if (this.compareBtn) {
      this.compareBtn.disabled = count < 2;
    }
  }

  // Restart
  restart() {
    // Reset state
    this.state.currentStep = 1;
    this.state.selections = {
      level: null,
      discipline: null,
      weight: null,
      budget: null,
    };
    if (!window.ExocetCompare) {
      this.state.compareList = [];
    }

    // Reset UI (cards: clear visual selection + aria-pressed for a11y)
    this.container.querySelectorAll(".bf-level-card, .bf-discipline-card, .bf-budget-card").forEach((card) => {
      card.classList.remove("is-selected");
      card.setAttribute("aria-pressed", "false");
    });

    // Reset compare buttons on any previously rendered product cards (fix P2-2)
    this.container.querySelectorAll(".bf-product-card__compare.is-active").forEach((btn) => {
      btn.classList.remove("is-active");
    });

    this.container.querySelectorAll('[data-action="next"], [data-action="results"]').forEach((btn) => {
      btn.disabled = btn.closest(".bf-step")?.dataset.stepType !== "weight" && !btn.classList.contains("bf-btn--skip");
    });

    // Reset weight slider (state.selections.weight stays in sync with slider default)
    if (this.weightSlider) {
      const defaultVal = this.weightDefault || 75;
      this.weightSlider.value = defaultVal;
      const min = parseInt(this.weightSlider.dataset.min) || 50;
      const max = parseInt(this.weightSlider.dataset.max) || 120;
      this.updateWeightDisplay(defaultVal, min, max);
      this.state.selections.weight = defaultVal;
    }

    // Hide results
    if (this.resultsScreen) {
      this.resultsScreen.classList.remove("is-active");
    }

    // Show first step
    this.goToStep(1);

    // Reset compare
    this.updateCompareBar();
  }

  // Overlay
  initOverlay() {
    const closeBtn = this.container.querySelector("[data-bf-close]");
    if (closeBtn) {
      this.listen(closeBtn, "click", () => this.closeOverlay());
    }

    this.listen(document, "keydown", (e) => {
      if (e.key === "Escape" && this.container.classList.contains("is-open")) {
        this.closeOverlay();
      }
    });

    this.listen(document, "board-finder:open", () => this.openOverlay());
  }

  openOverlay() {
    this.container.classList.add("is-open");
    document.body.style.overflow = "hidden";
    this.container.querySelector("[data-bf-close]")?.focus();
  }

  closeOverlay() {
    this.resultsRequestId++;
    this.container.removeAttribute("aria-busy");
    this.container.classList.remove("is-open");
    document.body.style.overflow = "";
    document.querySelector("[data-board-finder-trigger]")?.focus();
  }

  // Helpers
  capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

function initBoardFinder() {
  const container = document.querySelector('#BoardFinder');
  if (window.boardFinderInstance?.container === container) return;
  window.boardFinderInstance?.destroy();
  window.boardFinderInstance = container ? new BoardFinder(container) : null;
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initBoardFinder);
else initBoardFinder();
document.addEventListener("shopify:section:load", initBoardFinder);
document.addEventListener("shopify:section:unload", (event) => {
  const instance = window.boardFinderInstance;
  if (instance?.container && event.target.contains(instance.container)) {
    instance.destroy();
    window.boardFinderInstance = null;
  }
});
